import { useCallback, useState } from 'react'
import { useClient } from 'urql'
import { STATEMENT_SUMMARY_FOR_UNMATCH } from 'src/graphql/statements'

/** Hard block: locked statements or closed accounting period. Warning-only summaries are not blocked. */
export function isHardBlockedSummary(summary) {
  if (!summary) return false
  if (summary.blocked === false) return false
  return true
}

/**
 * Preflight para deshacer matches: si origin o end tienen meta.statementId,
 * el backend rechaza el unmatch. Esta query alimenta el recuadro informativo.
 */
export function useStatementUnmatchGuard(companyId) {
  const client = useClient()
  const [summary, setSummary] = useState(null)
  const [checking, setChecking] = useState(false)

  const fetchSummaryForPair = useCallback(
    async (originId, endId) => {
      const origin = String(originId || '').trim()
      const end = String(endId || '').trim()
      if (!companyId || !origin || !end) return null
      const result = await client
        .query(
          STATEMENT_SUMMARY_FOR_UNMATCH,
          { companyId, originId: origin, endId: end },
          { requestPolicy: 'network-only' },
        )
        .toPromise()
      if (result.error) return null
      return result?.data?.statementSummaryForUnmatch || null
    },
    [client, companyId],
  )

  const findBlockedSummary = useCallback(
    async (pairs = []) => {
      setChecking(true)
      try {
        for (const pair of pairs) {
          const summary = await fetchSummaryForPair(pair?.originId, pair?.endId)
          if (isHardBlockedSummary(summary)) return summary
        }
        return null
      } finally {
        setChecking(false)
      }
    },
    [fetchSummaryForPair],
  )

  const close = useCallback(() => setSummary(null), [])

  return {
    summary,
    setSummary,
    checking,
    fetchSummaryForPair,
    findBlockedSummary,
    close,
  }
}

export const inMatchPairsFromDocument = (document) => {
  const originId = document?._id || document?.id
  const inMatch = document?.meta?.matchs?.inMatch || document?.matchs?.inMatch || []
  return inMatch
    .map((match) => ({
      originId,
      endId: match?.id || match?.settleId || match?.docId || match?._id,
    }))
    .filter((pair) => pair.originId && pair.endId)
}
